import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../ui/select";
import { Plus, Network, Trash2 } from "lucide-react";
import { Device } from "../interfaces/EndpointInterfaces";
import { useToast } from "../../../hooks/use-toast";
import ConfirmationDialog from "./ConfirmationDialog";
import { myLocalStorage } from "../../StorageHelper";
import { addSubnets, deleteSubnets, listSubNets } from "./Api/subnetsapi";
import { Skeleton } from "../../ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/tooltip";
import { formatDate } from "../formatTimeAgo";

interface SubnetsTabProps {
    device: Device;
}



const cidrOptions = [
    "/8", "/9", "/10", "/11", "/12", "/13", "/14", "/15",
    "/16", "/17", "/18", "/19", "/20", "/21", "/22", "/23",
    "/24", "/25", "/26", "/27", "/28", "/29", "/30", "/31", "/32",
];

export default function SubnetsTab({ device }: SubnetsTabProps) {
    const [subnets, setSubnets] = useState<SubnetResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [newSubnet, setNewSubnet] = useState({ ipAddress: "", cidr: "/16" });
    const [deleteConfirmation, setDeleteConfirmation] = useState<{
        isOpen: boolean;
        subnet: string | null;
    }>({ isOpen: false, subnet: null });

    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    const { toast } = useToast();

    /** Fetch subnets */
    const fetchSubnets = async (device: Device) => {
        if (!tenantName.trim()) return;
        setLoading(true);
        try {
            const response: SubnetResponse = await listSubNets(tenantName, device.id);
            setSubnets(response);
        } catch (err) {
            console.error("Error fetching subnets:", err);
        } finally {
            setLoading(false);
        }
    };

    /** Add subnet */
    const handleAddSubnet = async () => {
        if (!newSubnet.ipAddress.trim()) {
            toast({
                title: "Validation Error",
                description: "Please enter a valid IP address",
                variant: "destructive",
            });
            return;
        }

        const ipRegex =
            /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        if (!ipRegex.test(newSubnet.ipAddress.trim())) {
            toast({
                title: "Invalid IP Address",
                description: "Please enter a valid IPv4 address",
                variant: "destructive",
            });
            return;
        }

        const payload = {
            tenantName: tenantName,
            id: device?.id,
            ip: `${newSubnet.ipAddress}${newSubnet.cidr}`,
        };

        let response = await addSubnets(payload);
        if (response) {
            toast({
                title: "Subnet Added",
                description: `Successfully added subnet ${newSubnet.ipAddress}${newSubnet.cidr}`,
            });
            fetchSubnets(device);
        }

        setIsAddDialogOpen(false);
        setNewSubnet({ ipAddress: "", cidr: "/16" });
    };

    /** Delete subnet */
    const handleDeleteSubnet = (subnet: string) => {
        setDeleteConfirmation({ isOpen: true, subnet });
    };

    const confirmDeleteSubnet = async () => {
        if (deleteConfirmation.subnet) {
            const payload = {
                tenantName: tenantName,
                id: device?.id,
                ip: deleteConfirmation.subnet,
            };

            let response = await deleteSubnets(payload);
            if (response) {
                toast({
                    title: "Subnet Deleted",
                    description: `Deleted subnet ${deleteConfirmation.subnet}`,
                });
                fetchSubnets(device);
            }
        }
        setDeleteConfirmation({ isOpen: false, subnet: null });
    };

    useEffect(() => {
        fetchSubnets(device);
    }, [device]);

    return (
        <div className="space-y-6 p-6" data-testid="subnets-tab">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-semibold text-foreground">Subnets</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Manage network subnets served by {device.name} gateway
                    </p>
                </div>

                <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="gap-2">
                            <Plus className="w-4 h-4" />
                            Add Subnet
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Add Subnet</DialogTitle>
                            <p className="text-sm text-muted-foreground">
                                Ensure that the subnet is pingable from this machine.
                            </p>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                            <div className="space-y-2">
                                <Label htmlFor="ip-address">IP Address</Label>
                                <Input
                                    id="ip-address"
                                    placeholder="Enter IP address"
                                    value={newSubnet.ipAddress}
                                    onChange={(e) =>
                                        setNewSubnet((prev) => ({
                                            ...prev,
                                            ipAddress: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="cidr">CIDR</Label>
                                <Select
                                    value={newSubnet.cidr}
                                    onValueChange={(value) =>
                                        setNewSubnet((prev) => ({ ...prev, cidr: value }))
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {cidrOptions.map((cidr) => (
                                            <SelectItem key={cidr} value={cidr}>
                                                {cidr}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setIsAddDialogOpen(false);
                                        setNewSubnet({ ipAddress: "", cidr: "/16" });
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button onClick={handleAddSubnet}>Submit</Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Network className="w-5 h-5" />
                        Configured Subnets ({subnets?.ipV4Addresses?.filter((addr) => addr.includes("/") && !addr.startsWith("100.64")).length ?? 0})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-6 space-y-4">
                            {/* Table header skeleton */}
                            <div className="flex items-center justify-between">
                                <Skeleton className="w-1/4 h-6" />
                                <Skeleton className="w-20 h-6" />
                            </div>

                            {/* Simulated table rows */}
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="flex items-center justify-between py-2">
                                    <Skeleton className="w-1/3 h-5 rounded" />
                                    <Skeleton className="w-16 h-5 rounded" />
                                    <Skeleton className="w-32 h-5 rounded" />
                                    <Skeleton className="w-20 h-8 rounded" />
                                </div>
                            ))}
                        </div>
                    ) : subnets && subnets.ipV4Addresses?.length > 0 ? (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50">
                                        <TableHead className="pl-6 font-semibold">
                                            IP Address
                                        </TableHead>
                                        <TableHead className="font-semibold">CIDR</TableHead>
                                        <TableHead className="font-semibold">Last Seen</TableHead>
                                        <TableHead className="pr-6 font-semibold">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {subnets.ipV4Addresses
                                        .filter(
                                            (addr) =>
                                                addr.includes("/") && !addr.startsWith("100.64")
                                        )
                                        .map((addr, index) => {
                                            const [ip, cidr] = addr.split("/");
                                            return (
                                                <TableRow key={index} className="hover:bg-muted/30">
                                                    <TableCell className="pl-6">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                                                <Network className="w-4 h-4 text-primary" />
                                                            </div>
                                                            <div className="font-mono font-medium">{ip}</div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                                                            {cidr ? `/${cidr}` : "-"}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatDate(new Date(subnets.lastSeen).toLocaleString())}
                                                    </TableCell>
                                                    <TableCell className="pr-6">

                                                        <Button
                                                            variant="destructive"
                                                            size="sm"
                                                            title={index === 0 || device.gatewayIpAddresses?.includes(addr)
                                                                ? "Cannot delete default gateway subnet and configured subnets"
                                                                : "Delete this subnet"}
                                                            disabled={index === 0 || device.gatewayIpAddresses?.includes(addr)}
                                                            onClick={() => handleDeleteSubnet(addr)}
                                                            className="gap-2"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                            Delete
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <div className="p-8 text-center">
                            <Network className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-lg font-medium mb-2">No Subnets Configured</h3>
                            <p className="text-muted-foreground mb-4">
                                No network subnets are currently configured for this gateway.
                            </p>
                            <Button onClick={() => setIsAddDialogOpen(true)} className="gap-2">
                                <Plus className="w-4 h-4" />
                                Add First Subnet
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Delete confirmation */}
            <ConfirmationDialog
                open={deleteConfirmation.isOpen}
                onOpenChange={(open) =>
                    !open && setDeleteConfirmation({ isOpen: false, subnet: null })
                }
                onConfirm={confirmDeleteSubnet}
                title="Delete Subnet"
                description={
                    deleteConfirmation.subnet
                        ? `Are you sure you want to delete subnet ${deleteConfirmation.subnet}?`
                        : ""
                }
                confirmText="Delete"
                variant="destructive"
            />
        </div>
    );
}
